/*
Usage Notes:

The following web component can be dropped into any standard Shopify theme (i.e. non-headless), using Liquid to pass product data:

  {% assign skioSellingPlanGroups = product.selling_plan_groups | where: 'app_id', 'SKIO' %}
  {% if skioSellingPlanGroups.size > 0 %}
    <skio-plan-picker
      product='{{ product | json | escape }}'
      selectedVariant='{{ product.selected_or_first_available_variant | json | escape }}'
      formId='{{ product_form_id }}'
      externalPriceSelector='#price-inner-button .money'
      externalPriceSelectorWithCurrency='[data-product-price-list] .money'
      currency='USD'
      useVariantInputClickEvents='false'
      variantInputSelector=''
      >
    </skio-plan-picker>
    <input type="hidden" aria-hidden="true" name="selling_plan" value="">
    <script src="{{ 'skio-plan-picker-component.js' | asset_url }}" type="module"></script>
    {% render 'skio-details' %}
  {% endif %}

  Note: formId not required if element is inside of a form already

  Example variantChanged event dispatch:
  document.dispatchEvent( new CustomEvent("variantChanged", { detail: { variantId: variant.id } }) );
*/

import {
  LitElement,
  html,
  css,
} from "https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js";

const skioStyles = css`
  .skio-plan-picker {
    display: flex;
    flex-direction: column;
    padding: 0;
    border: 0;
    margin-top: 50px;
    margin-bottom: 10px;
    gap: 8px;
  }
  .skio-onetime-second {
    order: 2;
  }

  .skio-group-container {
    display: block;
    margin: 0;
    padding: 0;
    cursor: pointer;
  }
  .skio-group-container--available {
    display: block;
    position: relative;
  }
  .skio-group-container--selected {
  }

  .skio-group-input {
    position: absolute;
    width: 0px;
    height: 0px;
    opacity: 0;
  }
  .skio-group-input:focus-visible ~ .skio-group-label {
    outline: 2px #ccc solid;
    outline-offset: 4px;
    border-radius: 5px;
  }

  .skio-group-label {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    padding: 16px;
    overflow: hidden;
    background-color: #e7e0da;
    border: none;
    border-radius: 8px;
    margin: 0;
    font-family: inherit;
    font-weight: 500;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* Give subscription container more padding */
  .skio-subscription-first .skio-group-label {
    padding: 16px 16px 20px 16px;
  }

  .skio-group-topline {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    font-size: 16px;
  }

  .skio-radio__container {
    display: flex;
    margin-right: 10px;
  }

  .skio-radio {
    transition:
      transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
      opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    transform-origin: center;
    transform: scale(0);
    opacity: 0;
  }
  .skio-group-label:hover .skio-radio {
    transform: scale(1);
    opacity: 0.75;
  }
  .skio-group-container--selected .skio-group-label {
    background-color: #192f37;
    color: white;
  }

  .skio-group-container--selected .skio-group-label .skio-radio {
    transform: scale(1);
    opacity: 1;
  }

  .skio-price {
    margin-left: auto;
  }

  .skio-group-content {
    width: auto;
    margin-left: 0;
    transition:
      max-height 0.25s cubic-bezier(0.4, 0, 0.2, 1),
      opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    max-height: 120px;
    opacity: 1;
  }

  /* Show gift message even when not selected, hide frequency dropdown */
  .skio-group-container:not(.skio-group-container--selected)
    .skio-group-content {
    max-height: 60px;
    opacity: 1;
    pointer-events: auto;
  }

  .skio-group-title {
    min-width: max-content;
  }

  .skio-frequency {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 8px 30px 8px 16px;
    margin-top: 8px;
    border-radius: 25px;
    background-color: white;
    width: 100%;
    border: 0;
    font-size: 14px;
    font-family: inherit;
    white-space: nowrap;
    text-overflow: ellipsis;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
    background-position: right 12px top 50%;
    background-size: 16px;
    background-repeat: no-repeat;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    cursor: pointer;
    height: 36px;
  }
  .skio-frequency.skio-frequency--one {
    background-image: none;
    pointer-events: none;
  }

  .skio-frequency span {
    text-transform: lowercase;
  }

  .skio-frequency-label {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 0px 16px 0px 16px;
    margin-top: 8px;
    border-radius: 25px;
    font-size: 14px;
    font-family: inherit;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  /* OR Divider Styling */
  .skio-divider {
    display: flex;
    align-items: center;
    text-align: center;
    margin: 12px 0;
    font-size: 14px;
    font-weight: 500;
    color: #666;
  }

  .skio-divider::before,
  .skio-divider::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #e5e5e5;
  }

  .skio-divider::before {
    margin-right: 16px;
  }

  .skio-divider::after {
    margin-left: 16px;
  }

  /* Ensure subscription is first */
  .skio-subscription-first {
    order: 1;
  }

  .skio-onetime-last {
    order: 3;
  }

  /* White HR and gift message styling */
  .skio-subscription-divider {
    height: 1px;
    background-color: rgba(255, 255, 255, 0.3);
    margin: 12px 0 10px 0;
    border: none;
  }

  /* Change HR color when not selected */
  .skio-group-container:not(.skio-group-container--selected)
    .skio-subscription-divider {
    background-color: rgba(0, 0, 0, 0.2);
  }

  .skio-gift-message {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
  }

  /* Change text color when not selected */
  .skio-group-container:not(.skio-group-container--selected)
    .skio-gift-message {
    color: rgba(0, 0, 0, 0.7);
  }

  .skio-gift-tick {
    width: 12px;
    height: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .skio-gift-tick svg {
    width: 8px;
    height: 8px;
    color: rgba(255, 255, 255, 0.9);
  }

  /* Change tick color when not selected */
  .skio-group-container:not(.skio-group-container--selected)
    .skio-gift-tick
    svg {
    color: rgba(0, 0, 0, 0.7);
  }

  /* Special pricing styling - change white elements to black */
  .skio-group-label.skio-special-pricing .skio-subscription-divider {
    background-color: rgba(0, 0, 0, 0.2);
  }

  .skio-group-label.skio-special-pricing .skio-gift-message {
    color: rgba(0, 0, 0, 0.7);
  }

  .skio-group-label.skio-special-pricing .skio-gift-tick svg {
    color: rgba(0, 0, 0, 0.7);
  }
`;

export class SkioPlanPickerComponentNew extends LitElement {
  static properties = {
    product: { type: Object }, //required
    productHandle: { type: String }, //optional (unless product isn't passed, then required)
    key: { type: String }, //optional, defaults to product.id; identifier for this instance of the Skio plan picker

    formId: { type: String }, //optional; if passed, used to connect input fields to form
    needsFormId: { type: Boolean }, //optional, defaults to false; if true, element needs to be passed a formId, else it searches for a form

    subscriptionFirst: { type: Boolean }, //optional, defaults to false; if true, shows subscription option above onetime
    startSubscription: { type: Boolean }, //optional, defaults to true; if true, auto-selects subscription on page load
    discountFormat: { type: String }, //optional, defaults to percent; can also pass "fixed"

    currency: { type: String }, //optional, defaults to 'USD', but can pass any 3 char identifier
    language: { type: String }, //optional, defaults to 'en-US', but can pass any similarly formatted language identifier
    moneyFormatter: {}, //placeholder for object

    externalPriceSelector: { type: String }, //optional, used to update the external price

    externalPriceSelectorWithCurrency: { type: String }, //optional, used to update the external price

    selectedVariant: { type: Object }, //placeholder for data
    skioSellingPlanGroups: {}, //placeholder for data
    availableSellingPlanGroups: {}, //placeholder for data
    selectedSellingPlanGroup: {}, //placeholder for data
    selectedSellingPlan: {}, //placeholder for data

    defaultFrequency: {}, //placeholder for data

    useVariantInputClickEvents: { type: Boolean }, // optional, allows use of variant input click events to update skio's selectedVariant
    variantInputSelector: {},
    highlightColor: { type: String }, // optional, hex color for subscription highlight background
    subscribeText: { type: String }, // optional, text label for subscribe button (default: "Subscribe for")
    onetimeText: { type: String }, // optional, text label for one-time button (default: "Buy one time")
    firstVariantLoaded: { type: Boolean },
  };

  static styles = skioStyles;

  constructor() {
    super();
    this.product = null;
    this.selectedVariant = null;

    this.productHandle = null;

    this.purchaseOption = "onetime";

    this.key = null;
    this.formId = null;
    this.needsFormId = false;

    this.skioSellingPlanGroups = [];
    this.availableSellingPlanGroups = [];

    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;

    this.startSubscription = true;
    this.subscriptionFirst = true;

    this.skioMainProduct = true;

    this.discountFormat = "percent";

    this.externalPriceSelector = "[skio-external-price]";

    this.externalPriceSelectorWithCurrency =
      "[skio-external-price-with-currency]";

    this.currency = Shopify.currency.active;
    this.language = "en-US";
    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: "currency",
      currency: this.currency,
    });

    this.defaultFrequency = null;

    this.useVariantInputClickEvents = null;
    this.variantInputSelector = null;
    this.firstVariantLoaded = null;
  }

  firstUpdated() {
    // Ensure correct initialization on mount
    if (!this.product || !this.selectedVariant) return;

    // Initialize skioSellingPlanGroups if not yet set
    if (
      !this.skioSellingPlanGroups ||
      this.skioSellingPlanGroups.length === 0
    ) {
      this.skioSellingPlanGroups =
        this.product.selling_plan_groups?.filter((g) => g.app_id === "SKIO") ||
        [];
      this.skioSellingPlanGroups.forEach((group) => {
        group.selling_plans?.sort(function (a, b) {
          if (
            parseInt(a.name.replace(/\D/g, "")) <
            parseInt(b.name.replace(/\D/g, ""))
          )
            return -1;
          if (
            parseInt(a.name.replace(/\D/g, "")) >
            parseInt(b.name.replace(/\D/g, ""))
          )
            return 1;
          if (
            parseInt(a.name.replace(/\D/g, "")) ==
            parseInt(b.name.replace(/\D/g, ""))
          )
            return 0;
        });
      });
    }

    // Compute available groups for the current variant
    this.availableSellingPlanGroups = this.skioSellingPlanGroups.filter(
      (group) =>
        group.selling_plans?.some((p) =>
          this.selectedVariant.selling_plan_allocations?.some(
            (a) => a.selling_plan_id === p.id,
          ),
        ),
    );

    // Seed each group's selected_selling_plan with an eligible plan
    this.availableSellingPlanGroups.forEach((group) => {
      const eligiblePlans =
        group.selling_plans?.filter((p) =>
          this.selectedVariant.selling_plan_allocations?.some(
            (a) => a.selling_plan_id === p.id,
          ),
        ) || [];

      if (this.defaultFrequency) {
        const preferred = eligiblePlans.find((x) =>
          x.name.toLowerCase().includes(this.defaultFrequency.toLowerCase()),
        );
        group.selected_selling_plan = preferred || eligiblePlans[0] || null;
      } else {
        group.selected_selling_plan = eligiblePlans[0] || null;
      }
    });

    // Auto-select subscription if applicable
    if (
      this.availableSellingPlanGroups.length > 0 &&
      (this.startSubscription === true ||
        this.product.requires_selling_plan === true ||
        this.purchaseOption === "subscription")
    ) {
      this.selectedSellingPlanGroup = this.availableSellingPlanGroups[0];
      this.selectedSellingPlan =
        this.availableSellingPlanGroups[0].selected_selling_plan;
      this.purchaseOption = "subscription";
    }

    // Sync external UI
    this.updateForm();
    this.updateExternalPrice();
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.startSubscription == true) {
      this.purchaseOption = "subscription";
    }

    if (!this.product && this.productHandle) {
      this.fetchProduct(this.productHandle);
    }

    if (this.needsFormId && this.formId == null) {
      let forms = document.querySelectorAll('form[action="/cart/add"]');
      if (forms.length > 0) {
        let form;
        forms.forEach((el) => {
          if (el.hasAttribute("skio-key")) {
            if (el.getAttribute("skio-key") == this.key) form = el;
          }
        });
        if (!form) form = forms[0];
        this.formId = form.id;
        this.requestUpdate();
      }
    }
    let skio = this;
    document.addEventListener("variantChanged", function (e) {
      //update variant id
      let variantId = e.detail.variantId;
      let variant = skio.product.variants.find((x) => x.id == variantId);
      if (variant) skio.selectedVariant = variant;
      else skio.log("Unable to find variant with id: ", variantId);
      skio.requestUpdate();
    });

    if (this.useVariantInputClickEvents) {
      document.addEventListener("load", skio.addVariantClickEventListeners());
    }
  }

  render() {
    if (
      !this.product ||
      !this.selectedVariant ||
      this.skioSellingPlanGroups.length == 0
    )
      return;

    // Update external prices after render completes
    this.updateComplete.then(() => {
      if (this.selectedSellingPlan) {
        this.updateExternalPrice();
      }
    });

    return html` <fieldset
      class="skio-plan-picker"
      skio-plan-picker="${this.key}"
    >
      <input
        ${this.formId !== null ? html`form="${this.formId}"` : ""}
        name="selling_plan"
        type="hidden"
        value="${this.selectedSellingPlan !== null
          ? this.selectedSellingPlan?.id
          : ""}"
      />
      <input
        ${this.formId !== null ? html`form="${this.formId}"` : ""}
        name="properties[Discount]"
        type="hidden"
        value="${this.selectedSellingPlan !== null
          ? this.discount(this.selectedSellingPlan).percent
          : ""}"
        ?disabled="${this.selectedSellingPlan == null ? true : false}"
      />

      ${this.availableSellingPlanGroups
        ? this.availableSellingPlanGroups.map(
            (group, index) => html`
              <div
                class="skio-group-container skio-group-container--available skio-subscription-first ${
                  this.selectedSellingPlanGroup == group
                    ? "skio-group-container--selected"
                    : ""
                }"
                skio-group-container
                @click=${() => this.selectSellingPlanGroup(group)}
              >
                <input
                  id="skio-selling-plan-group-${index}-${this.key}"
                  class="skio-group-input"
                  name="skio-group-${this.key}"
                  type="radio"
                  value="${group.id}"
                  skio-selling-plan-group="${group.id}"
                  ?checked=${
                    this.selectedSellingPlanGroup == group ? true : false
                  }
                />
                <label
                  class="skio-group-label ${
                    this.getProductSpecificPricingCopy()
                      ? "skio-special-pricing"
                      : ""
                  }"
                  for="skio-selling-plan-group-${index}-${this.key}"
                  style="${
                    this.getProductSpecificPricingCopy()
                      ? `background-color: ${this.highlightColor || '#ADFF2F'} !important; color: #000;`
                      : ""
                  }"
                >
                  <div class="skio-group-topline">
                    <div class="skio-radio__container">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          stroke-width="2"
                        ></circle>
                        <circle
                          class="skio-radio"
                          cx="12"
                          cy="12"
                          r="7"
                          fill="currentColor"
                        ></circle>
                      </svg>
                    </div>
                    <div class="skio-group-title">
                      ${(() => {
                        const pricingCopy =
                          this.getProductSpecificPricingCopy();
                        if (pricingCopy) {
                          const subText = (this.subscribeText || 'Subscribe for [price]').replace('[price]', pricingCopy.subscribePrice);
                          return html`
                            <span style="font-size: 16px; font-weight: 600;">
                              ${subText}
                            </span>
                          `;
                        } else {
                          return html`
                            ${group.name} ${""}
                            ${this.discount(group.selected_selling_plan)
                              .percent !== "0%"
                              ? html`
                                  <span>
                                    & save
                                    <span skio-discount
                                      >${this.discountFormat == "percent"
                                        ? this.discount(
                                            group.selected_selling_plan,
                                          ).percent
                                        : this.discount(
                                            group.selected_selling_plan,
                                          ).amount}</span
                                    >
                                  </span>
                                `
                              : html``}
                          `;
                        }
                      })()}
                    </div>
                  </div>
                  <div class="skio-group-content">
                    <!-- Frequency dropdown (only show when selected) -->
                    ${
                      this.selectedSellingPlanGroup == group
                        ? html`
                            ${(() => {
                              const eligiblePlans = group.selling_plans.filter(
                                (selling_plan) =>
                                  this.selectedVariant.selling_plan_allocations.some(
                                    (a) =>
                                      a.selling_plan_id === selling_plan.id,
                                  ),
                              );
                              const selectedPlan = group.selected_selling_plan;
                              const displayText =
                                group.name == "Subscription"
                                  ? `Delivery ${selectedPlan?.name.toLowerCase()}`
                                  : selectedPlan?.name;

                              // Ensure the selling plan is selected even if only one option
                              if (
                                eligiblePlans.length === 1 &&
                                selectedPlan &&
                                this.selectedSellingPlan !== selectedPlan
                              ) {
                                this.selectedSellingPlan = selectedPlan;
                                this.requestUpdate();
                              }

                              return eligiblePlans.length > 1
                                ? html`
                                    <select
                                      skio-selling-plans="${group.id}"
                                      class="skio-frequency"
                                      @change=${(e) =>
                                        this.selectSellingPlan(e.target, group)}
                                    >
                                      ${eligiblePlans.map(
                                        (selling_plan) => html`
                                          <option
                                            value="${selling_plan.id}"
                                            ?selected=${group.selected_selling_plan ==
                                            selling_plan}
                                          >
                                            ${group.name == "Subscription"
                                              ? `Delivery ${selling_plan.name.toLowerCase()}`
                                              : `${selling_plan.name}`}
                                          </option>
                                        `,
                                      )}
                                    </select>
                                  `
                                : html`
                                    <div class="skio-frequency-label">
                                      ${displayText}
                                    </div>
                                  `;
                            })()}
                          `
                        : html``
                    }
                    </div>
                  </div>
                </label>
              </div>
            `,
          )
        : ""}

      <div class="skio-divider" style="order: 2;">OR</div>

      <div
        class="skio-group-container skio-onetime-last ${this.product
          .requires_selling_plan == false
          ? "skio-group-container--available"
          : ""} ${this.selectedSellingPlanGroup == null
          ? "skio-group-container--selected"
          : ""}"
        skio-group-container
        @click=${() => this.selectSellingPlanGroup(null)}
      >
        <input
          id="skio-one-time-${this.key}"
          class="skio-group-input"
          name="skio-group-${this.key}"
          type="radio"
          value=""
          skio-one-time
          ?checked=${this.selectedSellingPlanGroup == null &&
          this.product.requires_selling_plan == false
            ? true
            : false}
        />
        <label class="skio-group-label" for="skio-one-time-${this.key}">
          <div class="skio-group-topline">
            <div class="skio-radio__container">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="2"
                ></circle>
                <circle
                  class="skio-radio"
                  cx="12"
                  cy="12"
                  r="7"
                  fill="currentColor"
                ></circle>
              </svg>
            </div>
            <div class="skio-group-title">${(this.onetimeText || 'Buy one time').replace('[price]', this.money(this.selectedVariant?.price ?? 0))}</div>
          </div>
        </label>
      </div>
    </fieldset>`;
  }

  updated = (changed) => {
    if (changed.has("product") && this.product) {
      //update key
      this.key = this.key ? this.key : this.product.id;

      //update skioSellingPlanGroups
      this.skioSellingPlanGroups = this.product.selling_plan_groups.filter(
        (selling_plan_group) => selling_plan_group.app_id === "SKIO",
      );

      // Debug logs disabled

      this.skioSellingPlanGroups.forEach((group) => {
        group.selling_plans.sort(function (a, b) {
          if (
            parseInt(a.name.replace(/\D/g, "")) <
            parseInt(b.name.replace(/\D/g, ""))
          )
            return -1;
          if (
            parseInt(a.name.replace(/\D/g, "")) >
            parseInt(b.name.replace(/\D/g, ""))
          )
            return 1;
          if (
            parseInt(a.name.replace(/\D/g, "")) ==
            parseInt(b.name.replace(/\D/g, ""))
          )
            return 0;
        });
      });

      // Log full eligible plans per variant (runs on initial product load)
      try {
        const plansByVariant = this.product.variants.map((v) => ({
          variant: { id: v.id, title: v.title },
          groups: this.skioSellingPlanGroups.map((g) => ({
            id: g.id,
            name: g.name,
            selling_plans: g.selling_plans
              ?.filter((p) =>
                v.selling_plan_allocations?.some(
                  (a) => a.selling_plan_id === p.id,
                ),
              )
              ?.map((p) => ({
                id: p.id,
                name: p.name,
                discount: this.discountForVariant(v, p),
              })),
          })),
        }));
        this.log(
          "available Skio selling plans by variant (JSON)",
          JSON.stringify(plansByVariant, null, 2),
        );
      } catch (e) {}
    }

    if (changed.has("selectedVariant") && this.selectedVariant) {
      if (this.selectedVariant.selling_plan_allocations.length == 0) {
        (this.selectedSellingPlan, (this.selectedSellingPlanGroup = null));
      }

      //update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      this.availableSellingPlanGroups = this.skioSellingPlanGroups.filter(
        (selling_plan_group) =>
          selling_plan_group.selling_plans.some((selling_plan) =>
            this.selectedVariant.selling_plan_allocations.some(
              (selling_plan_allocation) =>
                selling_plan_allocation.selling_plan_id === selling_plan.id,
            ),
          ),
      );

      // Only log eligible selling plans for the current selectedVariant
      try {
        const eligibleGroups = this.availableSellingPlanGroups.map((g) => ({
          id: g.id,
          name: g.name,
          selling_plans: g.selling_plans?.filter((p) =>
            this.selectedVariant?.selling_plan_allocations?.some(
              (a) => a.selling_plan_id === p.id,
            ),
          ),
        }));
        this.log(
          "available Skio selling plans (JSON)",
          JSON.stringify(eligibleGroups, null, 2),
        );
      } catch (e) {}

      // Also log for all variants (helps compare across variants on change)
      try {
        const plansByVariant = this.product.variants.map((v) => ({
          variant: { id: v.id, title: v.title },
          groups: this.skioSellingPlanGroups.map((g) => ({
            id: g.id,
            name: g.name,
            selling_plans: g.selling_plans
              ?.filter((p) =>
                v.selling_plan_allocations?.some(
                  (a) => a.selling_plan_id === p.id,
                ),
              )
              ?.map((p) => ({
                id: p.id,
                name: p.name,
                discount: this.discountForVariant(v, p),
              })),
          })),
        }));
        this.log(
          "available Skio selling plans by variant (JSON)",
          JSON.stringify(plansByVariant, null, 2),
        );
      } catch (e) {}

      // Suppressed: computed discounts log (only logging available selling plans per variant change)

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups?.length > 0) {
        //update each group with a default selected_selling_plan
        this.availableSellingPlanGroups.forEach((group) => {
          const eligiblePlans = group.selling_plans.filter((p) =>
            this.selectedVariant.selling_plan_allocations.some(
              (a) => a.selling_plan_id === p.id,
            ),
          );

          if (this.defaultFrequency) {
            // Prefer defaultFrequency if it is eligible for the current variant
            let preferred = eligiblePlans.find((x) =>
              x.name
                .toLowerCase()
                .includes(this.defaultFrequency.toLowerCase()),
            );
            group.selected_selling_plan = preferred || eligiblePlans[0] || null;
          } else {
            group.selected_selling_plan = eligiblePlans[0] || null;
          }
        });

        if (
          this.startSubscription == true ||
          this.product.requires_selling_plan == true ||
          this.purchaseOption == "subscription"
        ) {
          //find a matching selling plan, or choose first available

          if (
            this.selectedSellingPlan == null ||
            this.selectedSellingPlan == undefined
          ) {
            // Select the first available selling plan group and its first selling plan
            if (this.availableSellingPlanGroups.length > 0) {
              this.selectedSellingPlanGroup =
                this.availableSellingPlanGroups[0];
              this.selectedSellingPlan =
                this.availableSellingPlanGroups[0].selected_selling_plan;
              this.purchaseOption = "subscription";
              // Update external prices and form immediately after selection
              this.updateExternalPrice();
              this.updateForm();
            }
          } else {
            let sellingPlanName = this.selectedSellingPlan.name;

            let sellingPlanGroup = this.availableSellingPlanGroups.find((x) =>
              x.selling_plans.find((y) => y.name == sellingPlanName),
            );

            if (sellingPlanGroup) {
              let sellingPlan = sellingPlanGroup.selling_plans.find(
                (y) => y.name == sellingPlanName,
              );
              if (sellingPlan) {
                this.selectedSellingPlanGroup = sellingPlanGroup;
                this.selectedSellingPlan = sellingPlan;
                this.purchaseOption = "subscription";
                // Update external prices and form immediately after selection
                this.updateExternalPrice();
                this.updateForm();
              }
            } else {
              this.selectedSellingPlanGroup =
                this.availableSellingPlanGroups[0];
              this.selectedSellingPlan =
                this.availableSellingPlanGroups[0].selected_selling_plan;
              this.purchaseOption = "subscription";
              // Update external prices and form immediately after selection
              this.updateExternalPrice();
              this.updateForm();
            }
          }
        } else {
          (this.selectedSellingPlan, (this.selectedSellingPlanGroup = null));
        }
      }

      //update the form that was passed, if any
      this.updateForm();
      //ensure external price (e.g., Add to Cart button) reflects current variant/plan
      this.updateExternalPrice();
    }

    if (changed.has("selectedSellingPlan")) {
      //update price of price elements if applicable
      document
        .querySelectorAll(`[skio-price][skio-key="${this.key}"]`)
        .forEach((el) => {
          el.innerHTML = this.price(this.selectedSellingPlan);
        });

      //update display of external content elements
      document
        .querySelectorAll(`[skio-onetime-content][skio-key="${this.key}"]`)
        .forEach((el) => {
          this.selectedSellingPlan !== null
            ? (el.style.display = "none")
            : el.style.removeProperty("display");
        });

      document
        .querySelectorAll(`[skio-subscription-content][skio-key="${this.key}"]`)
        .forEach((el) => {
          this.selectedSellingPlan == null
            ? (el.style.display = "none")
            : el.style.removeProperty("display");
        });

      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true,
        composed: true,
        detail: {
          sellingPlan: this.selectedSellingPlan,
          key: this.key,
        },
      });

      this.dispatchEvent(event);

      //update the form that was passed, if any
      this.updateForm();
      this.updateExternalPrice();

      //update external selling_plan input value
      let sellingPlanInput = document.querySelector(
        'input[name="selling_plan"]',
      );
      this.selectedSellingPlan != null
        ? (sellingPlanInput.value = this.selectedSellingPlan.id)
        : (sellingPlanInput.value = "");
    }

    if (changed.has("formId")) {
      //update the form that was passed, if any
      this.updateForm();
    }
  };

  // Helper method to get product-specific pricing copy
  getProductSpecificPricingCopy() {
    if (!this.product || !this.selectedVariant) return null;

    const handle = this.product.handle;
    const variantTitle = this.selectedVariant.title?.toLowerCase() || "";

    // magic-mud: 8 oz bottle variant
    if (handle === "magic-mud" && variantTitle.includes("8 oz")) {
      return {
        subscribePrice: "$32",
      };
    }

    // magic-mud: 32 oz bottle variant
    if (handle === "magic-mud" && variantTitle.includes("32 oz")) {
      return {
        subscribePrice: "$72",
      };
    }

    // shampoo-light-bar: 4-pack variant (check this first before "bar only")
    if (handle === "shampoo-light-bar" && (variantTitle.includes("4-pack") || variantTitle.includes("4 pack") || variantTitle.includes("4pack"))) {
      return {
        subscribePrice: "$84",
      };
    }

    // shampoo-light-bar: bar only variant
    if (handle === "shampoo-light-bar" && variantTitle.includes("bar only")) {
      return {
        subscribePrice: "$22",
      };
    }

    return null;
  }

  log = (...args) => {
    args.unshift("%c🟡 [skio]", "color: #8770f2;");
    console.log.apply(console, args);
  };

  error = (...args) => {
    args.unshift("%c🟡 [skio]", "color: #ff0000");
    console.error.apply(console, args);
  };

  updateExternalPrice() {
    const priceElements = document.querySelectorAll(this.externalPriceSelector);
    const currencyElements = document.querySelectorAll(
      this.externalPriceSelectorWithCurrency,
    );

    // Calculate price - subscription price if plan selected, otherwise regular variant price
    let newPrice;
    if (this.selectedSellingPlan) {
      newPrice = this.price(this.selectedSellingPlan);
    } else {
      // One-time purchase - use regular variant price
      newPrice = this.money(this.selectedVariant.price);
    }

    priceElements.forEach((el) => {
      el.innerHTML = newPrice;
    });
    currencyElements.forEach((el) => {
      el.innerHTML = newPrice + " " + this.currency;
    });

    // Also update any Add to Cart button text that embeds a dollar amount
    try {
      const atcButtons = document.querySelectorAll(".add-to-cart-btn");
      atcButtons.forEach((btn) => {
        const text = btn.textContent || "";
        const updated = text.replace(
          /\$\d{1,3}(?:,\d{3})*(?:\.\d{2})?/g,
          newPrice,
        );
        if (updated !== text) btn.textContent = updated;
      });
    } catch (e) {}

    // Also try a delayed update in case DOM isn't ready
    setTimeout(() => {
      const delayedPriceElements = document.querySelectorAll(
        this.externalPriceSelector,
      );

      let delayedNewPrice;
      if (this.selectedSellingPlan) {
        delayedNewPrice = this.price(this.selectedSellingPlan);
      } else {
        delayedNewPrice = this.money(this.selectedVariant.price);
      }

      delayedPriceElements.forEach((el) => {
        el.innerHTML = delayedNewPrice;
      });

      // Delayed update for Add to Cart buttons as well
      try {
        const atcButtons = document.querySelectorAll(".add-to-cart-btn");
        atcButtons.forEach((btn) => {
          const text = btn.textContent || "";
          const updated = text.replace(
            /\$\d{1,3}(?:,\d{3})*(?:\.\d{2})?/g,
            delayedNewPrice,
          );
          if (updated !== text) btn.textContent = updated;
        });
      } catch (e) {}
    }, 100);

    if (this.product.handle == "shampoo-light-bar") {
      document.querySelectorAll(".variant-input").forEach((el) => {
        let title = el.querySelector("input").value;
        let subtext = el.querySelector(".subtext");

        if (title.includes("4 Pack")) {
          let variant = this.product.variants.find((variant) =>
            variant.title.includes("4 Pack"),
          );
          if (this.selectedSellingPlan) {
            subtext.innerHTML = `$${(
              parseInt(variant.selling_plan_allocations[0].price) / 100
            ).toFixed(2)} ($${(
              parseInt(variant.selling_plan_allocations[0].price) / 400
            ).toFixed(2)} per bar)`;
          } else {
            subtext.innerHTML = `$${variant.price / 100} ($${
              variant.price / 400
            } per bar)`;
          }
        } else if (title.includes("Only")) {
          if (this.selectedSellingPlan) {
            subtext.innerHTML = `$${(
              parseInt(
                this.product.variants[0].selling_plan_allocations[0].price,
              ) / 100
            ).toFixed(2)}`;
          } else {
            subtext.innerHTML = `$${
              this.product.variants.find((variant) =>
                variant.title.includes("Only"),
              ).price / 100
            }`;
          }
        }
      });
    }

    if (this.product.handle == "magic-mud") {
      document.querySelectorAll(".variant-input").forEach((el) => {
        let title = el.querySelector("input").value;
        let subtext = el.querySelector(".subtext");
        let variant = this.product.variants.find((v) => v.title === title);

        if (variant && subtext) {
          if (this.selectedSellingPlan) {
            // Use subscription price
            const subscriptionAllocation =
              variant.selling_plan_allocations.find(
                (allocation) =>
                  allocation.selling_plan_id === this.selectedSellingPlan.id,
              );
            if (subscriptionAllocation) {
              subtext.innerHTML = `$${(
                subscriptionAllocation.price / 100
              ).toFixed(2)}`;
            }
          } else {
            // Use regular variant price
            subtext.innerHTML = `$${(variant.price / 100).toFixed(2)}`;
          }
        }
      });
    }
  }

  // Update selected selling plan group; called on click of skio-group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group;
    this.selectedSellingPlan = group?.selected_selling_plan;
    if (group) this.purchaseOption = "subscription";
    else this.purchaseOption = "onetime";

    //update the form that was passed, if any
    this.updateForm();
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(element, group) {
    let selling_plan = group.selling_plans.find((x) => x.id == element.value);
    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
    } else
      this.log(
        "Error: couldn't find selling plan with id " +
          element.value +
          " for variant " +
          this.selectedVariant.id +
          " from product " +
          this.product.id +
          " : " +
          this.product.handle,
      );
  }

  // Formats integer value into money value
  money(price) {
    return this.moneyFormatter.format(price / 100.0);
  }

  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discount(selling_plan) {
    if (!selling_plan || !this.selectedVariant)
      return { percent: "0%", amount: 0 };

    // Prefer variant-specific allocation to determine plan price for this variant
    const allocation = this.selectedVariant.selling_plan_allocations?.find(
      (a) => a.selling_plan_id === selling_plan.id,
    );

    const basePrice = this.selectedVariant.price;
    const planPrice = allocation?.price;

    if (typeof basePrice !== "number" || typeof planPrice !== "number") {
      return { percent: "0%", amount: 0 };
    }

    const amount = basePrice - planPrice;
    const percent =
      basePrice > 0 ? `${Math.round((amount / basePrice) * 100)}%` : "0%";

    return { percent, amount };
  }

  // Calculates the variant's price for the given selling plan, returns a formatted money value (if desired)
  price(selling_plan, formatted = true) {
    if (!selling_plan || !this.selectedVariant) {
      return formatted
        ? this.money(this.selectedVariant?.price ?? 0)
        : (this.selectedVariant?.price ?? 0);
    }

    const allocation = this.selectedVariant.selling_plan_allocations?.find(
      (a) => a.selling_plan_id === selling_plan.id,
    );

    const planPrice = allocation?.price;
    const fallback =
      this.selectedVariant.price - this.discount(selling_plan).amount;
    const result = typeof planPrice === "number" ? planPrice : fallback;

    return formatted ? this.money(result) : result;
  }

  // Calculates discount for a specific variant and selling plan using that variant's allocations
  discountForVariant(variant, selling_plan) {
    if (!variant || !selling_plan)
      return { percent: "0%", amount: 0, basePrice: null, finalPrice: null };

    const allocation = variant.selling_plan_allocations?.find(
      (a) => a.selling_plan_id === selling_plan.id,
    );

    const basePrice = variant.price;
    const planPrice = allocation?.price;

    if (typeof basePrice !== "number" || typeof planPrice !== "number") {
      return {
        percent: "0%",
        amount: 0,
        basePrice,
        finalPrice: planPrice ?? null,
      };
    }

    const amount = basePrice - planPrice;
    const percent =
      basePrice > 0 ? `${Math.round((amount / basePrice) * 100)}%` : "0%";

    return { percent, amount, basePrice, finalPrice: planPrice };
  }

  // If a formId was passed, appends the necessary <input> elements to the form
  updateForm() {
    if (this.formId) {
      let form = document.querySelector(`#${this.formId}`);

      if (form) {
        let selling_plan_input = form.querySelector('[name="selling_plan"]');
        if (selling_plan_input) {
          selling_plan_input.value =
            this.selectedSellingPlan?.id !== undefined
              ? this.selectedSellingPlan?.id
              : null;
          selling_plan_input.disabled =
            this.selectedSellingPlan?.id !== undefined ? false : true;
        } else {
          selling_plan_input = document.createElement("input");
          selling_plan_input.type = "hidden";
          selling_plan_input.name = "selling_plan";
          selling_plan_input.value =
            this.selectedSellingPlan?.id !== undefined
              ? this.selectedSellingPlan?.id
              : null;
          selling_plan_input.disabled =
            this.selectedSellingPlan?.id !== undefined ? false : true;
          form.append(selling_plan_input);
        }

        let discount_input = form.querySelector(
          '[name="properties[Discount]"]',
        );
        if (discount_input) {
          discount_input.value =
            this.selectedSellingPlan?.id !== undefined
              ? this.discount(this.selectedSellingPlan).percent
              : null;
          discount_input.disabled =
            this.selectedSellingPlan?.id !== undefined ? false : true;
        } else {
          discount_input = document.createElement("input");
          discount_input.type = "hidden";
          discount_input.name = "properties[Discount]";
          discount_input.value =
            this.selectedSellingPlan?.id !== undefined
              ? this.discount(this.selectedSellingPlan).percent
              : null;
          discount_input.disabled =
            this.selectedSellingPlan?.id !== undefined ? false : true;
          form.append(discount_input);
        }
      } else {
        this.error(
          `Skio error: form ID is ${this.formId}, but no form with that ID was found.`,
        );
      }
    }
  }

  addVariantClickEventListeners() {
    let variantInputs = document.querySelectorAll(this.variantInputSelector);
    let skio = this;
    for (let el of variantInputs) {
      el.addEventListener("click", function (e) {
        // may need to replace with ID / e.target depending on client setup
        // may need to use different attribute depending on ^^
        // console.log("🟡", e.target, e.currentTarget);
        let variantTitle = e.currentTarget.querySelector("span").innerText;
        // console.log("🟡 variantTitle", variantTitle);
        if (skio.product.handle.includes("bar")) {
          skio.selectedVariant = skio.product.variants.find((variant) =>
            variant.title.includes(variantTitle),
          );
        } else if (skio.product.handle.includes("rinse")) {
          let title = variantTitle.replace(" pouch", "").replace(" bottle", "");
          skio.selectedVariant = skio.product.variants.find((variant) =>
            variant.title.includes(title),
          );
        } else {
          // console.log("🟡 Not bar, not rinse");
          // console.log("🟡 variantTitle", variantTitle);
          // console.log("🟡 skio.product.variants", skio.product.variants);
          skio.selectedVariant = skio.product.variants.find((variant) =>
            variant.title.toLowerCase().includes(variantTitle.toLowerCase()),
          );
          // console.log("🟡 skio.selectedVariant", skio.selectedVariant);
        }
        // console.log("🟡 skio.selectedVariant", skio.selectedVariant);
      });
    }
  }

  // Optional functions keep if necessary

  /**
   *
   *
   */

  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  addToCart(quantity) {
    const items = [
      {
        id: this.selectedVariant.id,
        quantity: quantity,
        ...(this.selectedSellingPlan && {
          selling_plan: this.selectedSellingPlan?.id,
        }),
      },
    ];

    fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ items }),
    })
      .then((response) => response.json())
      .then((response) => {
        // this.log("SKIO added item to cart: ", response);
        //dispatch CustomEvent to tell document that an item was added to cart
        const event = new CustomEvent(`skio::added-to-cart`, {
          bubbles: true,
          composed: true,
          detail: {
            response,
            key: this.key,
          },
        });

        this.dispatchEvent(event);
      })
      .catch((error) => {
        this.error(`SKIO ${this.key} error adding item to cart: `, error);
      });
  }

  fetchProduct = (handle) => {
    return fetch(`/products/${handle}.js`)
      .then((response) => response.json())
      .then((product) => {
        this.product = product;
        this.selectedVariant = product.variants[0];

        return product;
      });
  };
}

customElements.define("skio-plan-picker-new", SkioPlanPickerComponentNew);
